import React, { useEffect } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { Box, Button, TableFooter } from '@mui/material';
import { CalculationFunctionValuesEnum, MechanicFormValuesType, MechanicStepType } from 'entities/mechanic/model';
import { useMechanicStepDefaultValues } from 'entities/mechanic/lib';
import { UseFormSetValue } from 'react-hook-form';
import { useMechanicCreateRowDisabledRule } from 'entities/mechanic/lib/hooks';
import { useGetPrizeListQuery } from 'entities/prize';
import { MechanicStepTableEditedNumberRow } from './MechanicStepTableEditedNumberRow';
import { MechanicStepTableNoEditedTextRow } from './MechanicStepTableNoEditedTextRow';

type MechanicStepTablePropsType = {
  calculationFunction: CalculationFunctionValuesEnum;
  setValue: UseFormSetValue<MechanicFormValuesType>;
  rows: MechanicStepType[];
  isIndividual: boolean;
  isCycle: boolean;
};

const DEFAULT_COLUMN_WIDTH = 100;

export const MechanicStepTable = ({
  calculationFunction,
  setValue,
  rows,
  isIndividual,
  isCycle,
}: MechanicStepTablePropsType) => {
  const CF3_target = calculationFunction === CalculationFunctionValuesEnum.CF3;
  const CF4_target = calculationFunction === CalculationFunctionValuesEnum.CF4;
  const CF6_target = calculationFunction === CalculationFunctionValuesEnum.CF6;
  const [editableRow, setEditableRow] = React.useState<number | null>(null);
  const [creatableRow, setCreatableRow] = React.useState<number | null>(null);

  const { data: prizeList } = useGetPrizeListQuery({});

  const defaultValues = useMechanicStepDefaultValues(calculationFunction, isIndividual);
  const disabledRule = useMechanicCreateRowDisabledRule(rows.length, editableRow, calculationFunction, isCycle);

  const handleClickStopEdit = () => {
    setEditableRow(null);
    setCreatableRow(null);
  };

  useEffect(() => {
    setValue('steps', [], { shouldValidate: true, shouldDirty: true });
    handleClickStopEdit();
  }, [calculationFunction, isIndividual, isCycle]);

  const handleClickRemoveItem = (idx: number) => {
    setValue(
      'steps',
      rows.filter((item) => item.id !== idx),
      { shouldValidate: true, shouldDirty: true },
    );
  };

  const handleClickEditItem = (idx: number) => {
    setEditableRow(idx);
    setCreatableRow(null);
  };

  const handleClickSaveItem = (idx: number, item: MechanicStepType) => {
    handleClickStopEdit();

    const steps: MechanicStepType[] = rows.map((step) => {
      if (step.id === idx) {
        return item;
      }
      return step;
    });
    setValue('steps', steps, { shouldValidate: true, shouldDirty: true });
  };

  const handleClickAddNewItem = () => {
    const id = rows.length
      ? Math.max.apply(
          null,
          rows.map((item) => item.id),
        ) + 1
      : 0;

    const newItem: MechanicStepType = {
      id,
      target: defaultValues.target?.techValue,
      reward: defaultValues.reward?.techValue,
      ...(CF6_target && { reward_type: 'prize' }),
    };

    setValue('steps', [...rows, newItem], { shouldValidate: true, shouldDirty: true });

    setCreatableRow(id);
    setEditableRow(id);
  };

  return (
    <>
      <TableContainer component={Paper}>
        <Table sx={{ tableLayout: 'auto' }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: DEFAULT_COLUMN_WIDTH }}>{CF6_target ? 'Цена' : 'Цель'}</TableCell>
              {(CF3_target || CF4_target) && <TableCell sx={{ width: DEFAULT_COLUMN_WIDTH }}>Тип награды</TableCell>}
              <TableCell sx={{ width: DEFAULT_COLUMN_WIDTH }}>{CF6_target ? 'Товар' : 'Награда'}</TableCell>
              <TableCell sx={{ width: DEFAULT_COLUMN_WIDTH }}>Действия</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => {
              if (editableRow !== row.id) {
                return (
                  <MechanicStepTableNoEditedTextRow
                    key={row.id}
                    row={row}
                    defaultValues={defaultValues}
                    editableRow={editableRow}
                    removeItem={handleClickRemoveItem}
                    editItem={handleClickEditItem}
                    prizeData={prizeList?.items || []}
                    CF3_target={CF3_target}
                    CF4_target={CF4_target}
                    CF6_target={CF6_target}
                  />
                );
              }

              return (
                <MechanicStepTableEditedNumberRow
                  key={row.id}
                  creatableRow={creatableRow}
                  editableRow={editableRow}
                  row={row}
                  defaultValues={defaultValues}
                  removeItem={handleClickRemoveItem}
                  saveItem={handleClickSaveItem}
                  stopEdit={handleClickStopEdit}
                  editRow={handleClickEditItem}
                  calculationFunction={calculationFunction}
                  prizeData={prizeList?.items || []}
                  CF3_target={CF3_target}
                  CF4_target={CF4_target}
                  CF6_target={CF6_target}
                />
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <TableFooter
        component={() => (
          <Box style={{ padding: '10px 0' }}>
            <Button
              disabled={disabledRule}
              variant="contained"
              onClick={handleClickAddNewItem}
            >
              Добавить шаг
            </Button>
          </Box>
        )}
      />
    </>
  );
};
